import { ChangeEvent } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIContent, DCUIContentValidation } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { DCUIThemingTextContentValidationTypes } from "@enums/dcui-theming-text-content-validation-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { DCUIThemingTextContentValidationSectionValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { TextareaFormGroup } from "../../../../../common/textarea-form-group/textarea-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingTextContentValidationGenericSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    displayText: string;
    validationType: DCUIThemingTextContentValidationTypes;
    validationErrors: DCUIThemingTextContentValidationSectionValidationErrors;
    updateDCUIContentValidationFieldValue: (key: keyof DCUIContentValidation, value: string) => void;
};

export const DCUIThemingTextContentValidationGenericSection = (props: DCUIThemingTextContentValidationGenericSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        displayText,
        validationType,
        validationErrors,
        updateDCUIContentValidationFieldValue
    } = props;

    const handleDetectionSubheadingChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionSubheading`, event.target.value);
    };

    const handleResetDetectionSubheading = () => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionSubheading`, inheritedDCUIContent.validation[`${validationType}DetectionSubheading`]);
    };

    const handleDetectionLoadingTextChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionLoadingText`, event.target.value);
    };

    const handleResetDetectionLoadingText = () => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionLoadingText`, inheritedDCUIContent.validation[`${validationType}DetectionLoadingText`]);
    };

    const handleDetectionSuccessMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionSuccessMessage`, event.target.value);
    };

    const handleResetDetectionSuccessMessage = () => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionSuccessMessage`, inheritedDCUIContent.validation[`${validationType}DetectionSuccessMessage`]);
    };

    const handleDetectionErrorMessageChange = (event: ChangeEvent<HTMLInputElement>) => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionErrorMessage`, event.target.value);
    };

    const handleResetDetectionErrorMessage = () => {
        updateDCUIContentValidationFieldValue(`${validationType}DetectionErrorMessage`, inheritedDCUIContent.validation[`${validationType}DetectionErrorMessage`]);
    };

    const validationGenericResources = resources.pages.verificationServiceDetails.tabs.dcuiTheming.subTabs.textContent.sections.validation.sections.common;

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.validation[`${validationType}DetectionSubheading`]}
                currentThemingPropertyValue={dcuiContent.validation[`${validationType}DetectionSubheading`]}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={`document-capture-text-content-validation-${validationType}-detection-subheading-input`}
                    automationId={`document-capture-text-content-validation-${validationType}-detection-subheading-input`}
                    label={validationGenericResources.inputs.subheading.label(displayText)}
                    ariaLabel={validationGenericResources.inputs.subheading.label(displayText)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetectionSubheadingChange}
                    value={dcuiContent.validation[`${validationType}DetectionSubheading`] || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={validationGenericResources.inputs.subheading.tooltip(displayText)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetectionSubheading}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detectionTitleError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.validation[`${validationType}DetectionLoadingText`]}
                currentThemingPropertyValue={dcuiContent.validation[`${validationType}DetectionLoadingText`]}
                pageLevel={pageLevel}
            >
                <InputFormGroup
                    controlId={`document-capture-text-content-validation-${validationType}-detection-loading-text-input`}
                    automationId={`document-capture-text-content-validation-${validationType}-detection-loading-text-input`}
                    label={validationGenericResources.inputs.loadingText.label(displayText)}
                    ariaLabel={validationGenericResources.inputs.loadingText.label(displayText)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetectionLoadingTextChange}
                    value={dcuiContent.validation[`${validationType}DetectionLoadingText`] || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={validationGenericResources.inputs.loadingText.tooltip(displayText)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetectionLoadingText}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detectionLoadingTextError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.validation[`${validationType}DetectionSuccessMessage`]}
                currentThemingPropertyValue={dcuiContent.validation[`${validationType}DetectionSuccessMessage`]}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={`document-capture-text-content-validation-${validationType}-detection-success-message-input`}
                    automationId={`document-capture-text-content-validation-${validationType}-detection-success-message-input`}
                    label={validationGenericResources.inputs.successMessage.label(displayText)}
                    ariaLabel={validationGenericResources.inputs.successMessage.label(displayText)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetectionSuccessMessageChange}
                    value={dcuiContent.validation[`${validationType}DetectionSuccessMessage`] || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={validationGenericResources.inputs.successMessage.tooltip(displayText)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetectionSuccessMessage}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detectionSuccessTextError}

            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedDCUIContent.validation[`${validationType}DetectionErrorMessage`]}
                currentThemingPropertyValue={dcuiContent.validation[`${validationType}DetectionErrorMessage`]}
                pageLevel={pageLevel}
            >
                <TextareaFormGroup
                    controlId={`document-capture-text-content-validation-${validationType}-detection-error-message-input`}
                    automationId={`document-capture-text-content-validation-${validationType}-detection-error-message-input`}
                    label={validationGenericResources.inputs.errorMessage.label(displayText)}
                    ariaLabel={validationGenericResources.inputs.errorMessage.label(displayText)}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleDetectionErrorMessageChange}
                    value={dcuiContent.validation[`${validationType}DetectionErrorMessage`] || ""}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={validationGenericResources.inputs.errorMessage.tooltip(displayText)}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetDetectionErrorMessage}
                />
            </ThemingFormGroupWrapper>
            {validationErrors.detectionErrorTextError}
        </div>
    );
};